/**
 * Capture-time image processing.
 *
 * A 4 MB phone photo becomes ~300 KB. This is what makes a 1 GB free storage
 * tier last years instead of months, so it happens before anything touches the
 * network — not as a server-side optimisation that never gets built.
 */

const MAX_EDGE = 1600;
const QUALITY = 0.82;

export interface ProcessedImage {
  blob: Blob;
  width: number;
  height: number;
  mime: string;
}

export async function processImage(file: File): Promise<ProcessedImage> {
  const bitmap = await createImageBitmap(file);
  const scale = Math.min(1, MAX_EDGE / Math.max(bitmap.width, bitmap.height));
  const width = Math.round(bitmap.width * scale);
  const height = Math.round(bitmap.height * scale);

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('2D canvas unavailable — cannot process image');
  ctx.drawImage(bitmap, 0, 0, width, height);
  bitmap.close();

  const blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, 'image/webp', QUALITY),
  );
  // Every target this ships to encodes WebP; PNG is the honest fallback rather
  // than storing the untouched original and blowing the storage budget.
  if (!blob) {
    const png = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png'));
    if (!png) throw new Error('Canvas produced no image data');
    return { blob: png, width, height, mime: 'image/png' };
  }
  return { blob, width, height, mime: 'image/webp' };
}

export const isImage = (file: File) => file.type.startsWith('image/');

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
